import React, { useState, useEffect } from 'react';
import { UserCircle2 } from 'lucide-react';

import { useDanceStore } from '../../store/useDanceStore';
import { useAuthStore } from '../../store/useAuthStore';
import { ExplorerView } from './ExplorerView';
import { useTranslation } from 'react-i18next';
import { trackProfileEvent } from '../../lib/analytics';

export const ProfileModule: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore(state => state.user);
  const nickname = useAuthStore(state => state.nickname);
  const tier = useAuthStore(state => state.tier);
  const updateNickname = useAuthStore(state => state.updateNickname);
  const updateTier = useAuthStore(state => state.updateTier);
  const setActiveModule = useDanceStore(state => state.setActiveModule);
  const setToastMessage = useDanceStore(state => state.setToastMessage);

  const [inputNickname, setInputNickname] = useState(nickname || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to PoseDesigner if user logs out
  useEffect(() => {
    if (!user) {
      setActiveModule('PoseDesigner');
    }
  }, [user, setActiveModule]);

  // Track opening the profile module
  useEffect(() => {
    if (user) {
      trackProfileEvent('opened');
    }
  }, [user]);

  // Keep input in sync with store nickname (e.g. on load or initial value)
  useEffect(() => {
    if (nickname !== null) {
      setInputNickname(nickname);
    }
  }, [nickname]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedNickname = inputNickname.trim();
    if (!trimmedNickname) {
      setError(t('profile.error_empty'));
      return;
    }

    if (trimmedNickname.length > 20) {
      setError(t('profile.error_too_long'));
      return;
    }

    setSaving(true);
    try {
      await updateNickname(trimmedNickname);
      trackProfileEvent('nickname_updated', { nickname_length: trimmedNickname.length });
      setToastMessage(t('profile.success'));
    } catch (err: any) {
      setError(err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <UserCircle2 size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#2dd4bf' }}>
          {t('profile.title')}
        </h2>
        <p className="module-header-subtitle" style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>{t('profile.subtitle', { defaultValue: 'Manage your account' })}</p>
      </div>
    </div>
  );

  const isDirty = inputNickname.trim() !== (nickname ?? '');

  const content = (
    <div style={{ maxWidth: 480 }}>
      <form onSubmit={handleSave}>
        <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)', marginBottom: 8 }}>
          {/* Email row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
            <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>{t('profile.email')}</span>
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'inherit' }}>{user?.email || 'N/A'}</span>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />

          {/* Plan row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
            <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>{t('profile.plan')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                background: tier === 'pro' ? 'rgba(168, 85, 247, 0.15)' : tier === 'basic' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(45, 212, 191, 0.15)',
                color: tier === 'pro' ? '#c084fc' : tier === 'basic' ? '#38bdf8' : '#2dd4bf',
                padding: '1px 6px',
                borderRadius: '3px',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('profile.plan_' + tier, { defaultValue: tier })}
              </span>
              
              {/* Dev plan selector */}
              {import.meta.env.DEV && (
                <select
                  value={tier}
                  onChange={async (e) => {
                    const nextTier = e.target.value as any;
                    try {
                      await updateTier(nextTier);
                      setToastMessage(t('profile.tier_updated', 'Plan updated successfully (Dev Mode)'), 'info');
                    } catch (err: any) {
                      setToastMessage(err.message || 'Failed to update plan', 'error');
                    }
                  }}
                  style={{
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '4px',
                    color: '#fff',
                    fontSize: '0.65rem',
                    padding: '1px 4px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="registered">Registered</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                </select>
              )}
            </div>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />

          {/* Nickname row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0' }}>
            <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500, flexShrink: 0 }}>{t('profile.nickname')}</span>
            <input
              type="text"
              className="profile-input"
              value={inputNickname}
              onChange={(e) => setInputNickname(e.target.value)}
              placeholder={t('profile.nickname_placeholder')}
              disabled={saving}
              style={{ padding: '4px 8px', fontSize: '0.8rem', borderRadius: '6px', width: 'auto', maxWidth: 140 }}
            />
          </div>
          {error && <div className="profile-error-message" style={{ paddingBottom: 4 }}>{error}</div>}
        </div>

        <button
          type="submit"
          className="profile-save-btn"
          disabled={saving || !isDirty}
          style={{ padding: '4px 10px', fontSize: '0.78rem', borderRadius: '6px', margin: 0 }}
        >
          {saving ? t('profile.saving') : 'Update'}
        </button>
      </form>
    </div>
  );

  return <ExplorerView title={titleNode} content={content} />;
};
